import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import { hasActiveLiveTv } from '../lib/liveTv'
import '../components/prediction/predictionModern.css'
import { usePredictionUserEntry } from '../hooks/usePredictionUserEntry'
import { useScratchLotteryUserEntry } from '../hooks/useScratchLotteryUserEntry'
import { useHuayJackpotUserEntry } from '../hooks/useHuayJackpotUserEntry'
import './ModernDashboardPage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernDashboardPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

type MenuItem = {
  label: string
  href: string
  icon: string
}

/** หน้า Dashboard — ศูนย์ควบคุมสมาชิก (ธีม HTD) */
const ModernDashboardPage: React.FC<ModernDashboardPageProps> = ({
  themeConfig,
  assetConfig: _assetConfig,
}) => {
  const navigate = useNavigate()
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [userSummary, setUserSummary] = useState<authService.UserInfoSummary | null>(null)
  const [userBankAccounts, setUserBankAccounts] = useState<authService.UserBankAccount[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!authService.isAuthenticated()) return
    setLoading(true)
    Promise.all([
      walletService.getWallet(),
      authService.fetchUserInfoSummary(),
      authService.fetchUserBankAccounts(),
    ])
      .then(([walletRes, summary, bankAccounts]) => {
        if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
        else setWalletData(null)
        setUserSummary(summary ?? null)
        setUserBankAccounts(bankAccounts ?? [])
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const handleUserServiceAccessUpdated = () => {
      if (!authService.isAuthenticated()) return
      authService.fetchUserInfoSummary().then((summary) => {
        setUserSummary(summary ?? null)
      })
    }

    window.addEventListener('user_service_access_updated', handleUserServiceAccessUpdated)
    return () => window.removeEventListener('user_service_access_updated', handleUserServiceAccessUpdated)
  }, [])

  useEffect(() => {
    const handleBalanceNotification = () => {
      if (authService.isAuthenticated()) {
        walletService.getWallet().then((res) => {
          if (res.success && res.data) setWalletData(res.data)
        })
      }
    }
    window.addEventListener('balance_notification', handleBalanceNotification)
    return () => window.removeEventListener('balance_notification', handleBalanceNotification)
  }, [])

  const user = authService.getCurrentUser()
  const memberId = user?.username ?? '—'
  const displayName = user?.username ?? 'สมาชิก'
  const creditValue = walletData != null ? walletService.getDisplayBalance(walletData) : 0
  const creditText = walletService.formatCurrency(creditValue)
  const pointsValue = Number(userSummary?.points ?? 0)
  const pointsText = pointsValue.toLocaleString('th-TH', { maximumFractionDigits: 0 })
  const primaryBank = userBankAccounts[0] ?? null
  const tenantLotteryEnabled = themeConfig?.settings?.lotteryEnabled !== false
  const tenantCasinoEnabled = themeConfig?.settings?.casinoEnabled !== false
  const isLotteryEnabled =
    tenantLotteryEnabled && authService.isManagedServiceEnabled(userSummary?.serviceConfigs, 'lottery')
  const isCasinoEnabled =
    tenantCasinoEnabled && authService.isManagedServiceEnabled(userSummary?.serviceConfigs, 'casino')
  const predictionEnabled = themeConfig?.settings?.predictionEnabled === true
  const { showPredictionEntry } = usePredictionUserEntry(predictionEnabled)
  const scratchLotteryEnabled = themeConfig?.settings?.scratchLotteryEnabled === true
  const { showScratchLotteryEntry } = useScratchLotteryUserEntry(scratchLotteryEnabled)
  const huayJackpotEnabled = themeConfig?.settings?.huayJackpotEnabled === true
  const { showHuayJackpotEntry } = useHuayJackpotUserEntry(huayJackpotEnabled)
  const showLiveTv = hasActiveLiveTv(
    themeConfig?.settings?.liveTvChannels || [],
    themeConfig?.settings?.liveTvEnabled,
    themeConfig?.settings?.huayYoutubeLive
  )

  const menuItems: MenuItem[] = [
    { label: 'ประวัติฝาก-ถอน', href: '/history', icon: 'fas fa-exchange-alt' },
    { label: 'แนะนำเพื่อน', href: '/affiliate', icon: 'fas fa-user-friends' },
    { label: 'เครดิตเงินคืน', href: '/cashback', icon: 'fas fa-hand-holding-usd' },
    { label: 'โบนัส', href: '/promotion', icon: 'fas fa-gift' },
    ...(showLiveTv
      ? [{ label: 'ถ่ายทอดสด', href: '/live-tv', icon: 'fas fa-tv' }]
      : []),
    { label: 'ข้อมูลบัญชี', href: '/member', icon: 'fas fa-id-card' },
    { label: 'คู่มือ', href: '/howto', icon: 'fas fa-book' },
    { label: 'รางวัลประจำเดือน', href: '/reward', icon: 'fas fa-trophy' },
  ]

  const handleLogout = async () => {
    await authService.logout()
    navigate('/')
  }

  return (
    <div className="content htd-dash">
      <div className="htd-dash__glow" aria-hidden />
      <div className="htd-dash__inner">
        <header className="htd-dash__hero htd-dash-anim htd-dash-anim--1">
          <div className="htd-dash__avatar" aria-hidden>
            {(displayName || 'M').charAt(0).toUpperCase()}
          </div>
          <div className="htd-dash__hello">
            <div className="htd-dash__eyebrow-row">
              <p className="htd-dash__eyebrow">ศูนย์ควบคุม</p>
              <span className="htd-dash__status">ออนไลน์</span>
            </div>
            <h1 className="htd-dash__name">สวัสดีคุณ {displayName}</h1>
            <p className="htd-dash__member">
              รหัสสมาชิก <strong>{memberId}</strong>
            </p>
          </div>
        </header>

        <section className="htd-dash__panel htd-dash-anim htd-dash-anim--2" aria-label="สรุปบัญชี">
          <div className="htd-dash__wallet">
            <div className="htd-dash__tile htd-dash__tile--credit">
              <span className="htd-dash__tile-icon" aria-hidden>
                <i className="fas fa-wallet" />
              </span>
              <div className="htd-dash__tile-meta">
                <span>ยอดเครดิต</span>
                <strong className="htd-dash__tile-value" title={`${creditText} บาท`}>
                  <span className="htd-dash__amount">{loading ? '…' : creditText}</span>
                  {!loading ? <span className="htd-dash__unit">฿</span> : null}
                </strong>
              </div>
            </div>
            <div className="htd-dash__tile htd-dash__tile--points">
              <span className="htd-dash__tile-icon" aria-hidden>
                <i className="fas fa-star" />
              </span>
              <div className="htd-dash__tile-meta">
                <span>คะแนน</span>
                <strong className="htd-dash__tile-value" title={pointsText}>
                  <span className="htd-dash__amount">{loading ? '…' : pointsText}</span>
                </strong>
              </div>
            </div>
          </div>

          <Link to="/points" className="htd-dash__points-link">
            <i className="fas fa-gift" aria-hidden />
            <span>ดูคะแนนทั้งหมด / แลกรางวัล</span>
            <i className="fas fa-chevron-right" aria-hidden />
          </Link>

          <div className="htd-dash__money">
            <Link to="/topup" className="htd-dash__money-btn htd-dash__money-btn--deposit">
              <span className="htd-dash__money-ico" aria-hidden>
                <i className="fas fa-plus" />
              </span>
              <span>ฝากเงิน</span>
            </Link>
            <Link to="/withdraw" className="htd-dash__money-btn htd-dash__money-btn--withdraw">
              <span className="htd-dash__money-ico" aria-hidden>
                <i className="fas fa-minus" />
              </span>
              <span>ถอนเงิน</span>
            </Link>
          </div>
        </section>

        <section className="htd-dash__bank htd-dash-anim htd-dash-anim--3" aria-label="บัญชีธนาคาร">
          <div className="htd-dash__bank-head">
            <span>
              <i className="fas fa-university" aria-hidden />
              บัญชีรับเงิน
            </span>
            <Link to="/member" className="htd-dash__bank-edit">
              จัดการ
            </Link>
          </div>
          {primaryBank ? (
            <div className="htd-dash__bank-body">
              <strong>{primaryBank.bankName || 'ธนาคาร'}</strong>
              <span>{primaryBank.accountNumber}</span>
              {primaryBank.accountName ? <em>{primaryBank.accountName}</em> : null}
            </div>
          ) : (
            <div className="htd-dash__bank-empty">
              <p>ยังไม่ได้ผูกบัญชีรับเงิน</p>
              <Link to="/member">ไปกรอกข้อมูลบัญชี</Link>
            </div>
          )}
        </section>

        <section className="htd-dash__play htd-dash-anim htd-dash-anim--4" aria-label="เข้าเล่น">
          <h2 className="htd-dash__section-title">เข้าเล่นด่วน</h2>
          {isLotteryEnabled ? (
            <Link to="/lobby-huay" className="htd-dash__play-card htd-dash__play-card--lottery">
              <span className="htd-dash__play-icon" aria-hidden>
                <i className="fas fa-ticket-alt" />
              </span>
              <span className="htd-dash__play-body">
                <strong>เข้าแทงหวย</strong>
                <small>เลือกหวยที่เปิดรับแทง</small>
              </span>
              <i className="fas fa-chevron-right htd-dash__play-arrow" aria-hidden />
            </Link>
          ) : (
            <button
              type="button"
              className="htd-dash__play-card htd-dash__play-card--lottery is-disabled"
              disabled
              title={!tenantLotteryEnabled ? 'ปิดปรับปรุงระบบหวย' : 'ปิดการใช้งานหวยโดยผู้ดูแลระบบ'}
            >
              <span className="htd-dash__play-icon" aria-hidden>
                <i className="fas fa-ban" />
              </span>
              <span className="htd-dash__play-body">
                <strong>{!tenantLotteryEnabled ? 'ปิดปรับปรุงระบบ' : 'ปิดแทงหวย'}</strong>
                <small>ไม่สามารถเข้าเล่นได้ชั่วคราว</small>
              </span>
            </button>
          )}

          {isCasinoEnabled ? (
            <Link to="/lobby-casino" className="htd-dash__play-card htd-dash__play-card--casino">
              <span className="htd-dash__play-icon" aria-hidden>
                <i className="fas fa-dice" />
              </span>
              <span className="htd-dash__play-body">
                <strong>เข้าเล่นเกมส์</strong>
                <small>คาสิโน · สล็อต · อื่นๆ</small>
              </span>
              <i className="fas fa-chevron-right htd-dash__play-arrow" aria-hidden />
            </Link>
          ) : (
            <button
              type="button"
              className="htd-dash__play-card htd-dash__play-card--casino is-disabled"
              disabled
              title={!tenantCasinoEnabled ? 'ปิดปรับปรุงระบบเกมส์' : 'ปิดการใช้งานเกมส์โดยผู้ดูแลระบบ'}
            >
              <span className="htd-dash__play-icon" aria-hidden>
                <i className="fas fa-ban" />
              </span>
              <span className="htd-dash__play-body">
                <strong>{!tenantCasinoEnabled ? 'ปิดปรับปรุงระบบ' : 'ปิดเข้าเล่นเกมส์'}</strong>
                <small>ไม่สามารถเข้าเล่นได้ชั่วคราว</small>
              </span>
            </button>
          )}

          {showPredictionEntry ? (
            <Link to="/points/games/prediction" className="htd-dash__play-card htd-dash__play-card--pred">
              <span className="htd-dash__play-icon" aria-hidden>
                <i className="fas fa-bullseye" />
              </span>
              <span className="htd-dash__play-body">
                <strong>เกมทายผล Prediction</strong>
                <small>ทายผลลุ้นรางวัล · Odds Realtime</small>
              </span>
              <i className="fas fa-chevron-right htd-dash__play-arrow" aria-hidden />
            </Link>
          ) : null}

          {showScratchLotteryEntry ? (
            <Link
              to="/points/games/scratch-lottery"
              className="htd-dash__play-card htd-dash__play-card--scratch"
            >
              <span className="htd-dash__play-icon" aria-hidden>
                <i className="fas fa-ticket-alt" />
              </span>
              <span className="htd-dash__play-body">
                <strong>หวยขูดเมกา</strong>
                <small>ขูดลุ้นโชค · ทุกใบมีรางวัล</small>
              </span>
              <i className="fas fa-chevron-right htd-dash__play-arrow" aria-hidden />
            </Link>
          ) : null}

          {showHuayJackpotEntry ? (
            <Link
              to="/points/games/huay-jackpot"
              className="htd-dash__play-card htd-dash__play-card--huay-jackpot"
            >
              <span className="htd-dash__play-icon" aria-hidden>
                <i className="fas fa-bullseye" />
              </span>
              <span className="htd-dash__play-body">
                <strong>หวยแจ็กพอต</strong>
                <small>เลือก 5+1 · ออกผลทันที</small>
              </span>
              <i className="fas fa-chevron-right htd-dash__play-arrow" aria-hidden />
            </Link>
          ) : null}
        </section>

        <section className="htd-dash__menu htd-dash-anim htd-dash-anim--5" aria-label="เมนูเพิ่มเติม">
          <h2 className="htd-dash__section-title">เมนูเพิ่มเติม</h2>
          <div className="htd-dash__menu-grid">
            {menuItems.map((item) => (
              <Link key={item.href} to={item.href} className="htd-dash__menu-item">
                <span className="htd-dash__menu-icon" aria-hidden>
                  <i className={item.icon} />
                </span>
                <span className="htd-dash__menu-label">{item.label}</span>
                <i className="fas fa-chevron-right htd-dash__menu-chevron" aria-hidden />
              </Link>
            ))}
          </div>
        </section>

        <button type="button" className="htd-dash__logout htd-dash-anim htd-dash-anim--6" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt" aria-hidden />
          ออกจากระบบ
        </button>
      </div>
    </div>
  )
}

export default ModernDashboardPage
